// src/components/payroll/SalarySlipRequest.jsx
// Employees use this to request, view, and download their approved salary slips.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { payrollAPI, salarySlipAPI } from '../../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtC = (v) =>
  v != null
    ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '₹0.00';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_META = {
  PENDING:  { label: 'Pending Approval', color: '#b5882a', bg: '#fdf6e7', icon: '⏳' },
  APPROVED: { label: 'Approved',         color: '#3d9e6e', bg: '#ecf7f2', icon: '✅' },
  REJECTED: { label: 'Rejected',         color: '#c95f5f', bg: '#fdf0f0', icon: '❌' },
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  .ssr-wrap { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
  .ssr-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; margin-top: 16px; }
  .ssr-card {
    background: #fff; border: 1px solid #e2e6ed; border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,.06); overflow: hidden;
    transition: box-shadow .15s, transform .15s;
  }
  .ssr-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.1); transform: translateY(-1px); }
  .ssr-card-hdr { padding: 14px 18px; border-bottom: 1px solid #eaedf2; background: #f8f9fb; }
  .ssr-card-body { padding: 16px 18px; }
  .ssr-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-size: 13px; }
  .ssr-row:last-child { margin-bottom: 0; }
  .ssr-label { color: #9aa3b4; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; }
  .ssr-val   { color: #1e2636; font-weight: 600; }
  .ssr-badge {
    display: inline-block; padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 700;
  }
  .ssr-btn {
    padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
    cursor: pointer; border: none; font-family: inherit;
    transition: opacity .15s, transform .1s;
  }
  .ssr-btn:hover   { opacity: .88; }
  .ssr-btn:active  { transform: scale(.97); }
  .ssr-btn:disabled { opacity: .45; cursor: not-allowed; }
  .ssr-btn-primary { background: #4a7fc1; color: #fff; }
  .ssr-btn-ghost   { background: #eef0f4; color: #5a6478; }
  .ssr-btn-download { background: #3d9e6e; color: #fff; }
  .ssr-separator { height: 1px; background: #eaedf2; margin: 12px 0; }
  .ssr-empty { text-align: center; padding: 52px 20px; color: #9aa3b4; }
  .ssr-tabs  { display: flex; gap: 4px; border-bottom: 1px solid #e2e6ed; padding: 0 4px; margin-bottom: 20px; }
  .ssr-tab   {
    padding: 9px 18px; border: none; background: none; cursor: pointer;
    font-size: 13px; font-family: inherit; font-weight: 500; color: #9aa3b4;
    border-bottom: 2px solid transparent; transition: all .15s;
  }
  .ssr-tab.active { color: #4a7fc1; font-weight: 700; border-bottom-color: #4a7fc1; }
  .ssr-filter { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; align-items: center; }
  .ssr-select {
    padding: 7px 10px; border: 1px solid #e2e6ed; border-radius: 8px;
    font-size: 13px; background: #fff; color: #1e2636; outline: none; cursor: pointer;
  }
  .ssr-modal-overlay {
    position: fixed; inset: 0; background: rgba(30,38,54,.45);
    display: flex; align-items: center; justify-content: center;
    z-index: 200; backdrop-filter: blur(4px); padding: 16px;
  }
  .ssr-modal {
    background: #fff; border-radius: 16px; border: 1px solid #e2e6ed;
    width: 100%; max-width: 820px; max-height: 92vh;
    display: flex; flex-direction: column; overflow: hidden;
    box-shadow: 0 8px 32px rgba(0,0,0,.14);
  }
  .ssr-modal-hdr {
    padding: 16px 22px; border-bottom: 1px solid #e2e6ed;
    display: flex; align-items: center; justify-content: space-between;
    background: #f8f9fb;
  }
  .ssr-modal-body { padding: 22px; overflow-y: auto; flex: 1; }

  /* ── Printable salary slip ── */
  .slip-page {
    background: #fff; border: 1px solid #c5ccdb; border-radius: 10px;
    padding: 32px 36px; max-width: 720px; margin: 0 auto;
    font-size: 13px; color: #1e2636; line-height: 1.6;
  }
  .slip-logo-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
  .slip-title    { font-size: 22px; font-weight: 800; color: #4a7fc1; letter-spacing: -.02em; }
  .slip-subtitle { font-size: 12px; color: #9aa3b4; }
  .slip-period   { font-size: 13px; color: #1e2636; font-weight: 700; }
  .slip-divider  { border: none; border-top: 1px solid #e2e6ed; margin: 16px 0; }
  .slip-section  { margin-bottom: 18px; }
  .slip-section-title {
    font-size: 10px; font-weight: 800; letter-spacing: .12em;
    text-transform: uppercase; color: #4a7fc1; margin-bottom: 10px;
    padding-bottom: 4px; border-bottom: 2px solid #eef3fb;
  }
  .slip-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .slip-field  { background: #f8f9fb; border-radius: 6px; padding: 8px 12px; }
  .slip-field-label { font-size: 10px; color: #9aa3b4; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; }
  .slip-field-val   { font-size: 13px; color: #1e2636; font-weight: 600; margin-top: 2px; }
  .slip-table { width: 100%; border-collapse: collapse; }
  .slip-table th { text-align: left; padding: 7px 12px; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #9aa3b4; background: #f8f9fb; }
  .slip-table td { padding: 8px 12px; border-bottom: 1px solid #eaedf2; font-size: 13px; }
  .slip-table tr:last-child td { border-bottom: none; }
  .slip-net { background: #eef3fb; border-radius: 8px; padding: 14px 16px; display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }
  .slip-net-label { font-size: 14px; font-weight: 700; color: #1e2636; }
  .slip-net-val   { font-size: 22px; font-weight: 800; color: #3d9e6e; }
  .slip-approved-badge { background: #ecf7f2; border: 1px solid #b2dcc8; border-radius: 8px; padding: 10px 14px; font-size: 12px; color: #3d9e6e; margin-top: 18px; }
  .slip-footer { margin-top: 24px; font-size: 11px; color: #9aa3b4; text-align: center; }

  @media print {
    body * { visibility: hidden !important; }
    .slip-print-area, .slip-print-area * { visibility: visible !important; }
    .slip-print-area { position: fixed; inset: 0; padding: 20px; }
    .ssr-no-print { display: none !important; }
  }
`;

// ─── SlipCard ─────────────────────────────────────────────────────────────────
function SlipCard({ request, onView }) {
  const pr = request.payroll_detail || {};
  const sm = STATUS_META[request.status] || STATUS_META.PENDING;

  return (
    <div className="ssr-card">
      <div className="ssr-card-hdr">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e2636' }}>
              {MONTH_NAMES[pr.month] || '—'} {pr.year}
            </div>
            <div style={{ fontSize: 11, color: '#9aa3b4', marginTop: 2 }}>
              Requested {new Date(request.requested_at).toLocaleDateString('en-IN')}
            </div>
          </div>
          <span className="ssr-badge" style={{ color: sm.color, background: sm.bg, border: `1px solid ${sm.color}44` }}>
            {sm.icon} {sm.label}
          </span>
        </div>
      </div>
      <div className="ssr-card-body">
        <div className="ssr-row">
          <span className="ssr-label">Gross Salary</span>
          <span className="ssr-val">{fmtC(pr.gross_salary)}</span>
        </div>
        <div className="ssr-row">
          <span className="ssr-label">Total Deductions</span>
          <span className="ssr-val" style={{ color: '#c95f5f' }}>- {fmtC(pr.total_deductions)}</span>
        </div>
        {(pr.late_days > 0) && (
          <div className="ssr-row">
            <span className="ssr-label">Late Arrivals</span>
            <span className="ssr-val" style={{ color: '#b5882a' }}>{pr.late_days} day(s) ({pr.late_penalty_days} deducted)</span>
          </div>
        )}
        <div className="ssr-separator" />
        <div className="ssr-row">
          <span style={{ fontWeight: 700, color: '#1e2636', fontSize: 13 }}>Net Salary</span>
          <span style={{ fontWeight: 800, color: '#3d9e6e', fontSize: 16 }}>{fmtC(pr.net_salary)}</span>
        </div>

        {request.status === 'REJECTED' && request.rejection_reason && (
          <div style={{ background: '#fdf0f0', border: '1px solid #e8baba', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#c95f5f', marginTop: 10 }}>
            <strong>Rejection reason:</strong> {request.rejection_reason}
          </div>
        )}

        {request.status === 'APPROVED' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="ssr-btn ssr-btn-download" onClick={() => onView(request)}>
              📄 View &amp; Download Slip
            </button>
          </div>
        )}

        {request.status === 'PENDING' && (
          <div style={{ marginTop: 14, fontSize: 12, color: '#9aa3b4' }}>
            ⏳ Awaiting approval from Admin / Principal / Dean
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PayrollCard (for records without a slip request yet) ─────────────────────
function PayrollCard({ payroll, onRequest, requesting }) {
  const isRequested = !!payroll.slip_status;
  return (
    <div className="ssr-card">
      <div className="ssr-card-hdr">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e2636' }}>
              {payroll.month_name} {payroll.year}
            </div>
            <div style={{ fontSize: 11, color: '#9aa3b4', marginTop: 2 }}>
              Status: {payroll.status_display}
            </div>
          </div>
          {isRequested && (
            <span className="ssr-badge" style={{ color: '#4a7fc1', background: '#eef3fb', border: '1px solid #c5d8f0' }}>
              Slip Requested
            </span>
          )}
        </div>
      </div>
      <div className="ssr-card-body">
        <div className="ssr-row">
          <span className="ssr-label">Gross Salary</span>
          <span className="ssr-val">{fmtC(payroll.gross_salary)}</span>
        </div>
        <div className="ssr-row">
          <span className="ssr-label">Deductions</span>
          <span className="ssr-val" style={{ color: '#c95f5f' }}>- {fmtC(payroll.total_deductions)}</span>
        </div>
        {(payroll.late_days > 0) && (
          <div className="ssr-row">
            <span className="ssr-label">Late ({payroll.late_days} days)</span>
            <span className="ssr-val" style={{ color: '#b5882a' }}>
              - {fmtC(payroll.late_deduction)} ({payroll.late_penalty_days} day penalty)
            </span>
          </div>
        )}
        <div className="ssr-separator" />
        <div className="ssr-row">
          <span style={{ fontWeight: 700, color: '#1e2636', fontSize: 13 }}>Net Salary</span>
          <span style={{ fontWeight: 800, color: '#3d9e6e', fontSize: 16 }}>{fmtC(payroll.net_salary)}</span>
        </div>
        {!isRequested && ['CALCULATED', 'APPROVED', 'PAID'].includes(payroll.status) && (
          <div style={{ marginTop: 14 }}>
            <button
              className="ssr-btn ssr-btn-primary"
              onClick={() => onRequest(payroll.id)}
              disabled={requesting === payroll.id}
            >
              {requesting === payroll.id ? '⏳ Requesting…' : '📋 Request Salary Slip'}
            </button>
          </div>
        )}
        {payroll.status === 'DRAFT' && (
          <div style={{ marginTop: 14, fontSize: 12, color: '#9aa3b4' }}>
            Payroll must be calculated before requesting a slip.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Salary Slip Print View ───────────────────────────────────────────────────
function SlipPrintView({ request }) {
  const pr    = request.payroll_detail || {};
  const ld    = pr.leave_details || [];
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

  const maskAccount = (acc) => {
    if (!acc) return '—';
    return '•'.repeat(Math.max(0, acc.length - 4)) + acc.slice(-4);
  };

  const deductionRows = [];
  if (parseFloat(pr.leave_deduction || 0) > 0) {
    deductionRows.push({
      label: `Leave Deduction (${pr.unpaid_leave_days} unpaid day${pr.unpaid_leave_days !== 1 ? 's' : ''})`,
      amount: pr.leave_deduction,
    });
  }
  if (parseFloat(pr.late_deduction || 0) > 0) {
    deductionRows.push({
      label: `Late Arrival Penalty (${pr.late_days} late × ÷3 = ${pr.late_penalty_days} day${pr.late_penalty_days !== 1 ? 's' : ''})`,
      amount: pr.late_deduction,
    });
  }
  if (parseFloat(pr.other_deductions || 0) > 0) {
    deductionRows.push({ label: 'Other Deductions', amount: pr.other_deductions });
  }

  const bankDetail = request.payroll_detail?.bank_detail || {};

  return (
    <div className="slip-page slip-print-area">
      {/* Header */}
      <div className="slip-logo-row">
        <div>
          <div className="slip-title">🏥 Medical College ERP</div>
          <div className="slip-subtitle">Official Salary Slip — Confidential</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="slip-period">{pr.month_name} {pr.year}</div>
          <div style={{ fontSize: 11, color: '#9aa3b4' }}>Generated {today}</div>
        </div>
      </div>
      <hr className="slip-divider" />

      {/* Employee Info */}
      <div className="slip-section">
        <div className="slip-section-title">Employee Information</div>
        <div className="slip-fields">
          <div className="slip-field">
            <div className="slip-field-label">Full Name</div>
            <div className="slip-field-val">{request.user_name || '—'}</div>
          </div>
          <div className="slip-field">
            <div className="slip-field-label">Employee ID</div>
            <div className="slip-field-val" style={{ fontFamily: 'monospace' }}>{request.user_employee_id || '—'}</div>
          </div>
          <div className="slip-field">
            <div className="slip-field-label">Role</div>
            <div className="slip-field-val">{pr.user_subrole || pr.user_role || '—'}</div>
          </div>
          <div className="slip-field">
            <div className="slip-field-label">Department</div>
            <div className="slip-field-val">{request.user_department || pr.user_department || '—'}</div>
          </div>
          <div className="slip-field">
            <div className="slip-field-label">Salary Period</div>
            <div className="slip-field-val">{pr.month_name} {pr.year}</div>
          </div>
          <div className="slip-field">
            <div className="slip-field-label">Days in Month</div>
            <div className="slip-field-val">30</div>
          </div>
        </div>
      </div>

      {/* Earnings */}
      <div className="slip-section">
        <div className="slip-section-title">Earnings</div>
        <table className="slip-table">
          <thead>
            <tr>
              <th>Description</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Basic Monthly Salary</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtC(pr.base_salary)}</td>
            </tr>
            <tr style={{ background: '#f8f9fb' }}>
              <td style={{ fontWeight: 700 }}>Gross Salary</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtC(pr.gross_salary)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Deductions */}
      <div className="slip-section">
        <div className="slip-section-title">Deductions</div>
        {deductionRows.length === 0 ? (
          <p style={{ color: '#9aa3b4', fontSize: 13 }}>No deductions this month.</p>
        ) : (
          <table className="slip-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {deductionRows.map((d, i) => (
                <tr key={i}>
                  <td>{d.label}</td>
                  <td style={{ textAlign: 'right', color: '#c95f5f', fontWeight: 600 }}>
                    - {fmtC(d.amount)}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#fdf0f0' }}>
                <td style={{ fontWeight: 700 }}>Total Deductions</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#c95f5f' }}>
                  - {fmtC(pr.total_deductions)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Leave breakdown if any */}
      {ld.length > 0 && (
        <div className="slip-section">
          <div className="slip-section-title">Leave Details</div>
          <table className="slip-table">
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Deduction</th>
              </tr>
            </thead>
            <tbody>
              {ld.map((l, i) => (
                <tr key={i}>
                  <td>{l.leave_type_display}</td>
                  <td>{l.from_date}</td>
                  <td>{l.to_date}</td>
                  <td>{l.days_in_month}</td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, color: l.is_paid ? '#3d9e6e' : '#c95f5f' }}>
                      {l.is_paid ? 'Paid' : 'LOP'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', color: '#c95f5f' }}>
                    {parseFloat(l.deduction_amount || 0) > 0 ? `- ${fmtC(l.deduction_amount)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Late details */}
      {parseFloat(pr.late_days || 0) > 0 && (
        <div className="slip-section">
          <div className="slip-section-title">Late Arrival Summary</div>
          <div className="slip-fields">
            <div className="slip-field">
              <div className="slip-field-label">Late Arrivals This Month</div>
              <div className="slip-field-val">{pr.late_days} occurrence{pr.late_days !== 1 ? 's' : ''}</div>
            </div>
            <div className="slip-field">
              <div className="slip-field-label">Penalty Days (÷ 3 rule)</div>
              <div className="slip-field-val" style={{ color: '#c95f5f' }}>{pr.late_penalty_days} day{pr.late_penalty_days !== 1 ? 's' : ''}</div>
            </div>
            <div className="slip-field">
              <div className="slip-field-label">Daily Rate</div>
              <div className="slip-field-val">{fmtC(pr.daily_salary)}</div>
            </div>
            <div className="slip-field">
              <div className="slip-field-label">Late Deduction</div>
              <div className="slip-field-val" style={{ color: '#c95f5f' }}>- {fmtC(pr.late_deduction)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Net Salary */}
      <div className="slip-net">
        <span className="slip-net-label">💰 Net Salary Payable</span>
        <span className="slip-net-val">{fmtC(pr.net_salary)}</span>
      </div>

      {/* Approval stamp */}
      <div className="slip-approved-badge">
        ✅ <strong>Approved</strong> by {request.approved_by_name || 'Authorised Signatory'}{' '}
        {request.approved_at
          ? `on ${new Date(request.approved_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`
          : ''}
      </div>

      <div className="slip-footer">
        This is a computer-generated salary slip. No signature required. · Confidential — for {request.user_name} only.
      </div>
    </div>
  );
}

// ─── Slip Modal ───────────────────────────────────────────────────────────────
function SlipModal({ request, onClose }) {
  const printRef = useRef(null);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(`
      <html><head><title>Salary Slip — ${request.user_name}</title>
      <style>
        body { font-family: 'Segoe UI', system-ui, sans-serif; margin: 0; padding: 20px; color: #1e2636; }
        ${CSS.replace(/@media print[\s\S]*?}/g, '')}
      </style></head>
      <body>${content.innerHTML}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  return (
    <div className="ssr-modal-overlay" onClick={onClose}>
      <div className="ssr-modal" onClick={e => e.stopPropagation()}>
        <div className="ssr-modal-hdr">
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e2636' }}>
              Salary Slip — {request.payroll_detail?.month_name} {request.payroll_detail?.year}
            </div>
            <div style={{ fontSize: 12, color: '#9aa3b4', marginTop: 2 }}>
              Approved — ready to download
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="ssr-btn ssr-btn-download ssr-no-print" onClick={handlePrint}>
              🖨️ Print / Download PDF
            </button>
            <button className="ssr-btn ssr-btn-ghost ssr-no-print" onClick={onClose}>✕ Close</button>
          </div>
        </div>
        <div className="ssr-modal-body" ref={printRef}>
          <SlipPrintView request={request} />
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function SalarySlipRequest({ user }) {
  const [tab, setTab]             = useState('payroll');   // 'payroll' | 'requests'
  const [payrolls, setPayrolls]   = useState([]);
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [viewSlip, setViewSlip]   = useState(null);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState('');
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [pr, sr] = await Promise.all([
        payrollAPI.getPayrollRecords({ year: filterYear }),
        salarySlipAPI.getMyRequests(),
      ]);
      setPayrolls(Array.isArray(pr) ? pr : pr.results || []);
      setRequests(Array.isArray(sr) ? sr : sr.results || []);
    } catch (e) {
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterYear]);

  useEffect(() => { load(); }, [load]);

  const handleRequest = async (payrollId) => {
    setRequesting(payrollId);
    setError('');
    try {
      await salarySlipAPI.requestSlip(payrollId);
      setSuccess('Salary slip requested successfully! You will be notified once approved.');
      setTimeout(() => setSuccess(''), 5000);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'Failed to request slip.');
    } finally {
      setRequesting(null);
    }
  };

  const filteredRequests = requests.filter(r =>
    !filterStatus || r.status === filterStatus
  );

  // Build a set of payroll IDs that already have a slip request
  const requestedPayrollIds = new Set(requests.map(r => r.payroll_record));

  return (
    <div className="ssr-wrap">
      <style>{CSS}</style>

      {/* Alerts */}
      {error && (
        <div style={{ background: '#fdf0f0', border: '1px solid #e8baba', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#c95f5f', display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c95f5f', fontWeight: 700 }}>✕</button>
        </div>
      )}
      {success && (
        <div style={{ background: '#ecf7f2', border: '1px solid #b2dcc8', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#3d9e6e' }}>
          ✅ {success}
        </div>
      )}

      {/* Tabs */}
      <div className="ssr-tabs">
        <button className={`ssr-tab ${tab === 'payroll' ? 'active' : ''}`} onClick={() => setTab('payroll')}>
          📋 My Payroll Records
        </button>
        <button className={`ssr-tab ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>
          📄 My Slip Requests
          {requests.filter(r => r.status === 'APPROVED').length > 0 && (
            <span style={{ marginLeft: 6, background: '#3d9e6e', color: '#fff', fontSize: 10, borderRadius: 20, padding: '1px 6px', fontWeight: 700 }}>
              {requests.filter(r => r.status === 'APPROVED').length} ready
            </span>
          )}
        </button>
      </div>

      {/* ── Tab: Payroll Records ── */}
      {tab === 'payroll' && (
        <>
          <div className="ssr-filter">
            <select className="ssr-select" value={filterYear} onChange={e => setFilterYear(Number(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span style={{ fontSize: 13, color: '#9aa3b4' }}>
              {loading ? 'Loading…' : `${payrolls.length} record${payrolls.length !== 1 ? 's' : ''} found`}
            </span>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ width: 28, height: 28, border: '2px solid #e2e6ed', borderTop: '2px solid #4a7fc1', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto' }} />
            </div>
          ) : payrolls.length === 0 ? (
            <div className="ssr-empty">
              <div style={{ fontSize: 32, marginBottom: 8, opacity: .4 }}>📭</div>
              <p>No payroll records found for {filterYear}.</p>
            </div>
          ) : (
            <div className="ssr-grid">
              {payrolls.map(p => (
                <PayrollCard
                  key={p.id}
                  payroll={{ ...p, slip_status: requestedPayrollIds.has(p.id) ? true : p.slip_status }}
                  onRequest={handleRequest}
                  requesting={requesting}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Tab: Slip Requests ── */}
      {tab === 'requests' && (
        <>
          <div className="ssr-filter">
            <select className="ssr-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <span style={{ fontSize: 13, color: '#9aa3b4' }}>
              {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
            </span>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ width: 28, height: 28, border: '2px solid #e2e6ed', borderTop: '2px solid #4a7fc1', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto' }} />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="ssr-empty">
              <div style={{ fontSize: 32, marginBottom: 8, opacity: .4 }}>📂</div>
              <p>No slip requests yet. Go to "My Payroll Records" to request one.</p>
            </div>
          ) : (
            <div className="ssr-grid">
              {filteredRequests.map(r => (
                <SlipCard key={r.id} request={r} onView={setViewSlip} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Slip view/download modal */}
      {viewSlip && <SlipModal request={viewSlip} onClose={() => setViewSlip(null)} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}